import React, {useMemo} from 'react'
import styled from 'styled-components'
import {
  Checkbox,
  OuterLabel,
  PrimaryLabel,
  SecondaryLabel
} from '../../../components/library/form/checkbox'
import {
  List,
  ListColumns,
  Table,
  useListOrderBy
} from '../../../components/library/list'
import {defaultTheme} from '../../../components/library/theme'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {useFlags} from '../../../hooks/use-flags'
import {toTitleCase, upperCaseFirst} from '../../../lib/helpers'
import {OrganisationNode} from '../../../organisation'
import {Permissions} from './index'
import {PermissionDetail, permissionDetail, Resource} from './permission-detail'
import {
  BetaFlagMarker,
  PreviewFlagMarker
} from '../../../components/feature-flags'

type ResourcesOrderBy = 'LABEL_ASC' | 'LABEL_DESC'

const Radios = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;

  > label {
    padding: 20px 10px 10px;
    margin-bottom: 10px;
    border-radius: ${p => p.theme.borderRadius};
    border: 1px solid ${p => p.theme.grey10};
    align-items: flex-start;

    ${OuterLabel} {
      padding: 0px 0px 10px 20px;
    }
    ${PrimaryLabel} {
      white-space: normal;
    }
    ${SecondaryLabel} {
      white-space: normal;
    }
  }
`

const ActionLabel = styled.p`
  color: ${p => p.theme.grey50};
  font-size: ${p => p.theme.fontSizeS};
`

const Wrapper = styled.div`
  font-size: 16px;
  ${Table} {
    td {
      vertical-align: top;
      white-space: normal;
    }
    td:first-child {
      white-space: nowrap;
    }
    td:last-child {
      min-width: 400px;
    }
  }
  ${OuterLabel} {
    font-size: 16px;
  }
`

const useResources = (organisationId: string) => {
  const flags = useFlags(organisationId)

  const resources = useMemo(
    () =>
      Object.values(PermissionResource)
        .map(resource => {
          const detail = permissionDetail[resource]
          if (detail) {
            const status = detail.status
            if (status === 'preview' && !flags.featurePreview) {
              return undefined
            }

            return {
              label: upperCaseFirst(resource.toLowerCase().replace(/_/g, ' ')),
              ...detail,
              id: resource
            }
          }
          return undefined
        })
        .filter((detail): detail is Resource => detail !== undefined),
    [flags.featurePreview]
  )

  return resources
}

export const ListResources = ({
  organisation,
  onChange,
  permissions
}: {
  organisation: OrganisationNode
  onChange: (update: Permissions) => void
  permissions: Permissions
}) => {
  const resources = useResources(organisation.id)
  const canEdit = organisation.hasPermission(
    PermissionResource.CustomRole,
    PermissionAction.Edit
  )
  const [orderBy, setOrderBy] = useListOrderBy<ResourcesOrderBy>(['LABEL_DESC'])

  useMemo(() => {
    const [name, direction] = orderBy[0].split('_')
    switch (name) {
      case 'LABEL':
        resources.sort((a, b) => {
          if (a.label < b.label) {
            return direction === 'ASC' ? 1 : -1
          }
          if (a.label > b.label) {
            return direction === 'ASC' ? -1 : 1
          }
          return 0
        })
        break
      default:
        break
    }
  }, [orderBy, resources])

  const columns: ListColumns<
    {id: string; label: string} & PermissionDetail,
    ResourcesOrderBy
  > = [
    {
      id: 'resource',
      header: 'Resource',
      accessor: r => {
        return (
          <Checkbox
            style={{marginTop: '10px', fontWeight: 'bold'}}
            checked={!!permissions[r.id]?.active}
            label={
              <>
                {r.label}
                {r.status === 'preview' && <PreviewFlagMarker />}
                {r.status === 'beta' && <BetaFlagMarker />}
              </>
            }
            disabled={!canEdit}
            onChange={() => {
              const active = !permissions[r.id]?.active

              onChange({
                ...permissions,
                [r.id]: {
                  active,
                  action: permissions[r.id]?.action || 'VIEW'
                },
                // 👇 For now mirror changes in card template permissions to action flows
                // TODO remove this once we have transitioned the WB client away from card template tables
                ...(r.id === PermissionResource.CardTemplate
                  ? {
                      [PermissionResource.ActionFlow]: {
                        active,
                        action: permissions[r.id]?.action || 'VIEW'
                      }
                    }
                  : {})
              })
            }}
          />
        )
      },
      sortKey: 'LABEL_ASC'
    },
    {
      id: 'description',
      header: 'Description',
      accessor: r =>
        (
          <p
            style={{
              marginTop: '20px',
              minWidth: 200,
              maxWidth: 400
            }}
          >
            {r.description}
          </p>
        ) || null
    },
    {
      id: 'permission',
      header: 'Permission',
      accessor: r => {
        const actions = [
          PermissionAction.View,
          PermissionAction.Edit,
          PermissionAction.Admin
        ].filter(a => {
          const key = a.toLowerCase() as 'view' | 'edit' | 'admin'
          return r?.actions?.[key] !== false
        })

        return (
          <Radios>
            {actions.map(action => {
              const disabled = !permissions[r.id]?.active
              const value = permissions?.[r.id]?.action || null
              const checked = !disabled && value === action
              const key = action.toLowerCase() as 'view' | 'edit' | 'admin'
              const description = r.actions?.[key] ?? null
              return (
                <Checkbox
                  key={`${r.id}_${action}`}
                  asRadio={true}
                  disabled={!canEdit}
                  label={
                    <>
                      {toTitleCase(action)}
                      {!!description && (
                        <ActionLabel>{description}</ActionLabel>
                      )}
                    </>
                  }
                  checked={checked}
                  onChange={() =>
                    onChange({
                      ...permissions,
                      [r.id]: {
                        active: true,
                        action
                      },
                      // 👇 For now mirror changes in card template permissions to action flows
                      // TODO remove this once we have transitioned the WB client away from card template tables
                      ...(r.id === PermissionResource.CardTemplate
                        ? {
                            [PermissionResource.ActionFlow]: {
                              active: true,
                              action:
                                action === PermissionAction.Admin
                                  ? PermissionAction.Edit
                                  : action
                            }
                          }
                        : {})
                    })
                  }
                  style={{
                    ...(checked ? {backgroundColor: defaultTheme.grey05} : {})
                  }}
                />
              )
            })}
          </Radios>
        )
      }
    }
  ]
  return (
    <Wrapper>
      <List<{id: string; label: string} & PermissionDetail, ResourcesOrderBy>
        bleed={'#f2f2f2'}
        columns={columns}
        rows={resources}
        setOrderBy={setOrderBy}
        orderBy={orderBy}
      />
    </Wrapper>
  )
}
